// @ts-check
import Link from '@material-ui/core/Link';
import { TRACKING } from '../dashboard.doraV2.freeTier.consts';
import { TRACKING as TRACKING_EVENTS } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';

/**
 * @returns {JSX.Element}
 */
const QuickStartGuide = () => (
  <>
    <div>Welcome to your DORA Dashboard!</div>
    <br />

    <div>
      To get started, we recommend using LinearB's benchmarking feature to
      identify the slowest legs of your SDLC.
    </div>
    <br />

    <div>
      Click on the colored icon next to any average in your metrics dashboard
      (red or orange icons are a great place to start optimizing) to see where
      your team stacks up relative to industry peers.
    </div>
    <br />

    <div>
      All in-app benchmarking data is compiled from a study of 3.6M+ PRs from
      2,800 dev teams across 64 countries.{' '}
      <Link
        href='https://linearb.io/resources/software-engineering-benchmarks-report'
        target='_blank'
        onClick={onLinkClick}
      >
        See the full report here.
      </Link>
    </div>
    <br />

    <div>
      P.S. You can click on any spike or dip in your data for a more granular
      view of all the PRs that contributed to any given average.
    </div>
    <br />
  </>
);

/**
 * @returns {JSX.Element}
 */
const ImprovingYourMetrics = () => (
  <>
    <div>
      DORA metrics alone don't improve dev teams. Without applying workflow
      automation to your bottlenecks, you're just staring at numbers instead of
      actively unclogging your delivery pipeline.
    </div>
    <br />

    <div>
      <Link target='_blank' href='https://linearb.io/platform/gitstream'>
        gitStream
      </Link>{' '}
      is our 100% free workflow automation tool that applies policy rules to
      your repositories that classify and route your PRs down specific paths.
      And repos that connect to gitStream improve their cycle time by an average
      of 61%.
    </div>
    <br />

    <div>
      The easiest place to start is with{' '}
      <Link
        href='https://docs.gitstream.cm/automations/provide-estimated-time-to-review/'
        target='_blank'
        onClick={onLinkClick}
      >
        estimated review time labels
      </Link>
      , which help developers gain context into reviews and prioritize their
      time accordingly.
    </div>
    <br />

    <div>
      For 10 more workflow automations that can improve your DORA metrics, check
      out our{' '}
      <Link
        href='https://linearb.io/resources/continuous-merge-guide-to-dora-metrics'
        target='_blank'
        onClick={onLinkClick}
      >
        Continuous Merge Guide to DORA Metrics
      </Link>
      .
    </div>
    <br />
  </>
);

/**
 * @returns {JSX.Element}
 */
const EngineeringLeaders = () => (
  <>
    <div>Hey Engineering Leader 👋</div>
    <br />
    <div>
      LinearB is here to help you answer two key questions:
      <ul>
        <li>Is my team performing efficiently?</li>
        <li>Is our effort aligned to business goals?</li>
      </ul>
    </div>
    <br />
    <div>
      DORA metrics and their leading indicators are a great first step to
      understanding your organization's performance.
    </div>
    <br />
    <div>
      The next step in your engineering metrics program is to gain visibility
      into resource allocation and project predictability. You can begin
      exploring these views via the Projects tab above.
    </div>
    <br />
    <div>
      When you’re ready to open the entire LinearB platform and start a free
      trial, please use this link to book a full platform walk-through with our
      team:{' '}
      <Link
        href='https://linearb.io/book-a-demo'
        target='_blank'
        onClick={onLinkClick}
      >
        Schedule Here
      </Link>
    </div>
    <br />
  </>
);

const ResourceLibrary = () => (
  <>
    <div>
      Check out some of our most popular resources, including exclusive
      research, tactical guides, and ready-to-use presentation templates for
      engineering leaders looking to build a continuous improvement engine.
    </div>
    <br />

    <div>
      <Link
        target='_blank'
        href='https://linearb.io/resources/cto-board-slides'
        onClick={onLinkClick}
      >
        CTO Board Deck Template:
      </Link>{' '}
      Everything you need to present your case for engineering investment,
      including data on business impact, resource allocation, and execution
      metrics.
    </div>
    <br />

    <div>
      <Link
        target='_blank'
        href='https://linearb.io/resources/engineering-leader-guide-to-building-a-metrics-program'
        onClick={onLinkClick}
      >
        Engineering Leader's Guide To Building A Metrics Program:
      </Link>{' '}
      A tactical guide walking you through how to build an engineering metrics
      program from the ground up.
    </div>
    <br />

    <div>
      <Link
        target='_blank'
        onClick={onLinkClick}
        href='https://linearb.io/resources/dora-report'
      >
        2023 State of DevOps Report:
      </Link>{' '}
      The DORA team at Google and LinearB have partnered to gather research from
      over 36,000 professionals worldwide for the 2023 Accelerate State of
      DevOps Report.{' '}
    </div>
    <br />

    <div>
      <Link
        href='https://linearb.helpdocs.io/'
        target='_blank'
        onClick={onLinkClick}
      >
        Help Docs:
      </Link>{' '}
      Dive into how LinearB works with a getting started guide, troubleshooting
      tips, a metrics guide and more.
    </div>
    <br />
  </>
);

/**
 * Tracks link click events
 * return {void}
 */
const onLinkClick = () => {
  TrackingService.logEvent(TRACKING.EVENTS.CTA_CLICK, {
    page: TRACKING_EVENTS.PAGES.DASHBOARD,
    cta: TRACKING.CTA_TYPE.LINK,
    intent: TRACKING.CTA_INTENT.EDUCATION,
    source_url: window.location.href
  });
};

export const educationalTabsContent = /** @type {const} */ ([
  {
    title: 'Quickstart Guide',
    content: QuickStartGuide
  },
  {
    title: 'Improving Your Metrics',
    content: ImprovingYourMetrics
  },
  {
    title: 'Engineering Leaders',
    content: EngineeringLeaders
  },
  {
    title: 'Resource Library',
    content: ResourceLibrary
  }
]);
